import React from 'react';
import { Grid, makeStyles, Typography, IconButton } from '@material-ui/core';
import InputIcon from '@material-ui/icons/Input';
import { connect } from 'react-redux';
import i18n from 'i18n';
import { useHistory } from 'react-router';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import { CompanyData, PartnerData } from 'modules/authenticate/interfaces';
import { modalClose } from 'modules/modal/actions';
import { getFlavorEnvironmentsRequest } from 'modules/monitor/actions';
import {
  FlavorEnvironmentsData,
  FlavorEnvironmentsFields,
  FlavorsCostFields,
  PeriodCountAccess,
} from 'modules/monitor/interfaces';
import ApplicationState from 'store/interfaces';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import { getFlavorDisplayName } from 'utils/flavorDisplay';
import { formatCurrency } from 'utils/helpers';
import { getCompanyName } from 'utils/tsHelpers';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

interface FlavorEnvironmentsProps {
  isLoading: boolean;
  flavorEnvironmentsList: FlavorEnvironmentsData;
  flavorSelected: FlavorsCostFields;
  intervalTime: PeriodCountAccess;
  getFlavorEnvironmentsRequest: (props) => void;
  modalClose: () => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
}

const FlavorEnvironments = ({
  isLoading,
  flavorEnvironmentsList,
  flavorSelected,
  intervalTime,
  getFlavorEnvironmentsRequest,
  modalClose,
  selectCompany,
  selectPartner,
}: FlavorEnvironmentsProps) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    handleOrder,
    handlePageChange,
    handleSearch,
    setSearchValue,
    searchValue,
  } = useTableFilters(initialQuery);
  const history = useHistory();

  const headers: Array<TableCellHeaderProps> = [
    {
      key: 'access_environment_cost',
      i18n: i18n.t('ACCESS_KEY'),
      centerTitle: true,
    },
    {
      key: 'environment_name',
      i18n: i18n.t('ENVIRONMENT_KEY'),
      order: true,
    },
    {
      key: 'num_instances',
      i18n: i18n.t('INSTANCES_KEY'),
      order: true,
      centerTitle: true,
      textAlignCenter: true,
    },
    {
      key: 'total_cost',
      i18n: i18n.t('COSTS_PAGE.COST_KEY'),
      order: true,
    },
  ];

  React.useEffect(() => {
    getFlavorEnvironmentsRequest({
      // eslint-disable-next-line @typescript-eslint/camelcase
      flavor_name: flavorSelected.flavor,
      period: intervalTime,
      ...query,
    });
  }, [getFlavorEnvironmentsRequest, flavorSelected, query, intervalTime]);

  const redirectToContext = (item: FlavorEnvironmentsFields) => {
    selectCompany({
      id: item?.company_uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    selectPartner({
      id: item?.partner_uuid,
      name: item?.partner_name,
      shortName: item?.partner_name,
    });
    history.push(`/environments/${item?.environment_uuid}/performance/costs`);
  };

  return (
    <Grid>
      <Grid container className={classes.modalTitle}>
        <Typography>
          {i18n.t('COSTS_PAGE.SELECTED_FLAVOR_KEY')}
          {':'}
        </Typography>
        <Typography className={classes.flavorName}>
          {getFlavorDisplayName(flavorSelected?.flavor)}
        </Typography>
      </Grid>
      <Grid item>
        <SearchBar
          id="search-environments"
          isLoading={isLoading}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
      </Grid>

      <TableItems
        header={
          <>
            {headers?.map(header => (
              <TableCell
                key={header?.key}
                centerTitle={header?.centerTitle}
                textAlignCenter={header?.textAlignCenter}
                order={header?.order}
                markOderItem={itemOrder === header?.key}
                handleOrder={() =>
                  header?.order ? handleOrder(header?.key) : null
                }
              >
                {header?.i18n}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: FlavorEnvironmentsFields) => (
          <TableRow>
            <TableCell centerTitle textAlignCenter>
              <IconButton
                className={classes.accessButton}
                onClick={() => {
                  modalClose();
                  redirectToContext(item);
                }}
              >
                <InputIcon />
              </IconButton>
            </TableCell>
            <TableCell>{item?.environment_name}</TableCell>
            <TableCell centerTitle>
              {item?.num_instances.toLocaleString()}
            </TableCell>
            <TableCell>{formatCurrency(item?.total_cost)}</TableCell>
          </TableRow>
        )}
        data={flavorEnvironmentsList}
        onChangePage={handlePageChange}
        onChangeLimit={() => null}
        isLoading={isLoading}
        notFoundText={i18n.t('COSTS_PAGE.NO_RESULTS')}
        overrideRowsCount={10}
        notRowsPerPage
      />
    </Grid>
  );
};

const mapStateToProps = ({
  monitor: {
    flavorEnvironmentsIsLoading,
    flavorEnvironmentsData,
    flavorsCostPeriodFilter,
  },
}: ApplicationState) => ({
  isLoading: flavorEnvironmentsIsLoading,
  flavorEnvironmentsList: flavorEnvironmentsData,
  intervalTime: flavorsCostPeriodFilter,
});

export default connect(mapStateToProps, {
  selectCompany,
  selectPartner,
  getFlavorEnvironmentsRequest,
  modalClose,
})(FlavorEnvironments);
